import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from './LanguageContext';
import { Check, ChevronDown } from 'lucide-react';
import { OrderModal } from './OrderModal';
import { MapPinIcon, ClockIcon, PhoneIcon } from './Icons';
export function PackagesSection() {
  const {
    t
  } = useLanguage();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<any | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };
  const handleBuyNow = (unit: any) => {
    setSelectedPackage(unit);
    setIsModalOpen(true);
  };
  return <>
      <section id="packages" className="py-28 bg-brand-dark relative overflow-hidden" data-id="element-772">
        {/* Subtle background glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-brand-red/[0.03] rounded-full blur-[120px] pointer-events-none" data-id="element-773" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-774">
          <div className="text-center mb-16" data-id="element-775">
            {/* Web exclusive badge - above title */}
            <motion.div initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="inline-flex items-center gap-2 mb-5 px-4 py-2 rounded-full bg-brand-yellow/10 border border-brand-yellow/20 text-brand-yellow text-xs font-bold uppercase tracking-widest" data-id="element-776">
              <span className="relative flex h-2 w-2" data-id="element-777">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-yellow opacity-75" data-id="element-778"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-yellow" data-id="element-779"></span>
              </span>
              {t.packages.webExclusive}
            </motion.div>

            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="font-oswald text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 uppercase tracking-tight" data-id="element-780">
              {t.packages.title}
            </motion.h2>

            <motion.p initial={{
            opacity: 0,
            y: 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="text-lg text-gray-400 max-w-2xl mx-auto leading-relaxed" data-id="element-781">
              {t.packages.subtitle}
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 xl:gap-8" data-id="element-782">
            {t.packages.units.map((unit: any, index: number) => {
            const isExpanded = expandedId === unit.id;
            return <motion.div key={unit.id} initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="relative flex flex-col bg-[#111111] rounded-3xl border border-white/10 hover:border-white/20 transition-all duration-500 group hover:-translate-y-2 hover:shadow-[0_20px_40px_-15px_rgba(220,38,38,0.15)] overflow-hidden" data-id="element-783">
                  {/* Top gradient accent line */}
                  <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-brand-red to-brand-yellow opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-784" />

                  <div className="p-8 flex-grow flex flex-col" data-id="element-785">
                    <div className="mb-8" data-id="element-786">
                      <h3 className="font-oswald text-2xl font-bold text-white uppercase tracking-wide mb-2" data-id="element-787">
                        {unit.name}
                      </h3>
                      <p className="text-gray-400 text-sm leading-relaxed min-h-[40px]" data-id="element-788">
                        {unit.desc}
                      </p>
                    </div>

                    <div className="mb-8" data-id="element-789">
                      <div className="flex items-baseline text-white" data-id="element-790">
                        <span className="text-3xl font-bold text-gray-500 mr-1.5" data-id="element-791">
                          $
                        </span>
                        <span className="font-oswald text-6xl font-bold tracking-tight" data-id="element-792">
                          {unit.price}
                        </span>
                      </div>
                      {unit.competitorPrice && <div className="mt-3 inline-flex items-center text-sm font-medium text-gray-500" data-id="element-793">
                          <span className="line-through decoration-brand-red/50 decoration-2" data-id="element-794">
                            {t.packages.vsCompetitors.replace('{price}', unit.competitorPrice)}
                          </span>
                        </div>}
                    </div>

                    <button onClick={() => toggleExpand(unit.id)} className="flex items-center justify-between w-full py-3.5 px-5 bg-white/[0.03] hover:bg-white/[0.08] rounded-xl text-sm font-medium text-gray-300 transition-colors mb-6 border border-white/5" data-id="element-795">
                      <span data-id="element-796">{t.packages.includes}</span>
                      <motion.div animate={{
                    rotate: isExpanded ? 180 : 0
                  }} transition={{
                    duration: 0.3,
                    ease: 'easeInOut'
                  }} data-id="element-797">
                        <ChevronDown className="w-4 h-4 text-gray-400" data-id="element-798" />
                      </motion.div>
                    </button>

                    <AnimatePresence initial={false} data-id="element-799">
                      {isExpanded && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.3,
                    ease: 'easeInOut'
                  }} className="overflow-hidden" data-id="element-800">
                          <ul className="space-y-4 mb-8 pt-2" data-id="element-801">
                            {unit.features.map((feature: string, i: number) => <li key={i} className="flex items-start gap-3 text-sm text-gray-300" data-id="element-802">
                                <Check className="w-5 h-5 text-brand-red flex-shrink-0 mt-0.5" strokeWidth={2.5} data-id="element-803" />
                                <span className="leading-relaxed" data-id="element-804">
                                  {feature}
                                </span>
                              </li>)}
                          </ul>
                        </motion.div>}
                    </AnimatePresence>

                    <div className="mt-auto pt-2" data-id="element-805">
                      <button onClick={() => handleBuyNow(unit)} className="w-full py-4 rounded-xl font-oswald font-bold text-lg tracking-wider uppercase transition-all duration-300 bg-white text-brand-black hover:bg-brand-red hover:text-white shadow-[0_0_20px_rgba(255,255,255,0.05)] hover:shadow-[0_0_30px_rgba(220,38,38,0.3)] transform active:scale-[0.98]" data-id="element-806">
                        {t.packages.buyNow}
                      </button>
                    </div>
                  </div>
                </motion.div>;
          })}
          </div>

          {/* Closing Notes */}
          {t.packages.closingNotes && <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-807">
              {t.packages.closingNotes.map((note: string, i: number) => {
            const icons = [<MapPinIcon className="w-5 h-5 text-brand-red flex-shrink-0" data-id="element-808" />, <ClockIcon className="w-5 h-5 text-brand-yellow flex-shrink-0" data-id="element-809" />, <PhoneIcon className="w-5 h-5 text-brand-red flex-shrink-0" data-id="element-810" />];
            return <div key={i} className="flex items-start gap-3 bg-white/[0.03] border border-white/10 rounded-xl px-5 py-4" data-id="element-811">
                    {icons[i]}
                    <p className="text-gray-400 text-sm leading-relaxed" data-id="element-812">
                      {note}
                    </p>
                  </div>;
          })}
            </motion.div>}
        </div>
      </section>

      <OrderModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} selectedPackage={selectedPackage} data-id="element-813" />
    </>;
}